import React, { useState, useEffect } from 'react';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, Package, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  const navigate = useNavigate();
  const [removingId, setRemovingId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleRemoveItem = (id) => {
    setRemovingId(id);
    setTimeout(() => {
      removeFromCart(id);
      setRemovingId(null);
    }, 300);
  };

  const handleClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const total = getCartTotal();
  const [shippingMethod, setShippingMethod] = useState('mondial');

  // Estimate product weight (kg) based on category or explicit `weight` property if provided
  const categoryWeightEstimates = {
    'Lessive': 5.0,
    'Produit de ménage': 1.0,
    'Liquide vaisselle': 0.8,
    'Déodorant': 0.15,
    'Hygiène corporelle': 0.25,
    'Couche Pampers': 1.2,
    'Cosmétique': 0.25,
    'Serviette hygiénique': 0.1,
    'Entretien du linge': 0.6,
    'Entretien vaisselle': 0.6,
    'Produit WC': 0.5,
    'Entretien courant': 0.5,
    'Entretien maison': 1.0,
    'Divers bazar': 0.5,
    'default': 0.5,
  };

  const getEstimatedWeight = (item) => {
    // If product provides a weight (kg), use it
    if (item.weight && typeof item.weight === 'number' && item.weight > 0) return item.weight;
    // Try category-based estimate
    const cat = item.category || '';
    return categoryWeightEstimates[cat] ?? categoryWeightEstimates['default'];
  };

  const getTotalWeight = () => {
    return cartItems.reduce((sum, it) => {
      const qty = it.cartQuantity ?? it.quantity ?? 1;
      const w = getEstimatedWeight(it) || 0;
      return sum + (w * qty);
    }, 0);
  };

  const totalWeight = getTotalWeight();

  // Shipping cost model by carrier: flat rate up to X kg, otherwise base + perKg * weight
  const shippingOptions = {
    mondial: { flatKg: 2, flatRate: 4.0, base: 1.0, perKg: 1.2 },
    colissimo: { flatKg: 1, flatRate: 5.0, base: 1.5, perKg: 1.0 },
    dpd: { flatKg: 1.5, flatRate: 5.5, base: 1.8, perKg: 0.95 },
  };

  const calculateShipping = (weight, method) => {
    if (!method || weight <= 0) return 0;
    const opt = shippingOptions[method];
    if (!opt) return 0;
    if (weight <= opt.flatKg) return opt.flatRate;
    return +(opt.base + opt.perKg * weight).toFixed(2);
  };

  const calculatedShipping = total > 0 ? calculateShipping(totalWeight, shippingMethod) : 0;
  // Carrier-specific maximum fractions of subtotal
  const carrierMaxFraction = { mondial: 0.15, colissimo: 0.17, dpd: 0.205 };
  const maxFraction = carrierMaxFraction[shippingMethod] ?? 0.05;
  const fraisLivraison = total > 0 ? Math.min(calculatedShipping, total * maxFraction) : 0;
  const totalFinal = total + fraisLivraison;

  const styles = {
    container: {
      width: '100%',
      minHeight: '100vh',
      background: '#f8f9fa',
      paddingBottom: '3rem',
    },
    hero: {
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      padding: isMobile ? '2.5rem 1rem' : isTablet ? '3rem 2rem' : '4rem 2rem',
      color: '#ffffff',
      textAlign: 'center',
    },
    heroTitle: {
      fontSize: isMobile ? '1.8rem' : isTablet ? '2.5rem' : '3rem',
      fontWeight: '800',
      marginBottom: '1rem',
      fontFamily: "'Montserrat', sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem',
    },
    heroSubtitle: {
      fontSize: isMobile ? '0.95rem' : '1.2rem',
      color: '#cccccc',
    },
    mainContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '1.5rem 1rem' : isTablet ? '2rem 1.5rem' : '3rem 2rem',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '2rem',
      alignItems: 'flex-start',
    },
    cartSection: {
      flex: 1,
      width: '100%',
    },
    cartHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    cartTitle: {
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      fontWeight: '700',
      color: '#000000',
    },
    clearButton: {
      background: 'transparent',
      border: '2px solid #ff4444',
      color: '#ff4444',
      padding: isMobile ? '0.6rem 1rem' : '0.7rem 1.2rem',
      borderRadius: '10px',
      fontSize: isMobile ? '0.85rem' : '0.95rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    cartItems: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    cartItem: {
      background: '#ffffff',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '1rem' : '1.5rem',
      display: 'flex',
      gap: isMobile ? '1rem' : '1.5rem',
      boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      border: '2px solid transparent',
      position: 'relative',
      overflow: 'hidden',
    },
    itemImage: {
      width: isMobile ? '80px' : '120px',
      height: isMobile ? '80px' : '120px',
      borderRadius: '12px',
      objectFit: 'cover',
      flexShrink: 0,
    },
    itemDetails: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    itemCategory: {
      color: '#c59b00',
      fontSize: isMobile ? '0.75rem' : '0.85rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    itemName: {
      fontSize: isMobile ? '1rem' : '1.2rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.3rem',
    },
    itemQuantity: {
      color: '#666666',
      fontSize: isMobile ? '0.8rem' : '0.9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem',
    },
    itemPrice: {
      fontSize: isMobile ? '1.2rem' : '1.4rem',
      fontWeight: '800',
      color: '#c59b00',
      marginTop: 'auto',
    },
    itemActions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: '0.5rem',
    },
    deleteButton: {
      background: 'transparent',
      border: 'none',
      color: '#ff4444',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    quantityControl: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      background: '#f0f0f0',
      borderRadius: '10px',
      padding: '0.3rem',
    },
    quantityButton: {
      background: '#ffffff',
      border: 'none',
      width: isMobile ? '28px' : '32px',
      height: isMobile ? '28px' : '32px',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      color: '#000000',
    },
    quantityValue: {
      fontSize: isMobile ? '0.95rem' : '1rem',
      fontWeight: '700',
      minWidth: isMobile ? '30px' : '35px',
      textAlign: 'center',
      color: '#000000',
    },
    summarySection: {
      width: isMobile ? '100%' : isTablet ? '350px' : '400px',
      position: isMobile ? 'relative' : 'sticky',
      top: isMobile ? 'auto' : '100px',
    },
    summaryCard: {
      background: '#ffffff',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '1.5rem' : '2rem',
      boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
      border: '2px solid #c59b00',
    },
    summaryTitle: {
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '1.5rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #f0f0f0',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      fontSize: isMobile ? '0.95rem' : '1rem',
    },
    summaryLabel: {
      color: '#666666',
      fontWeight: '500',
    },
    summaryValue: {
      color: '#000000',
      fontWeight: '700',
    },
    totalRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px solid #f0f0f0',
    },
    totalLabel: {
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      fontWeight: '700',
      color: '#000000',
    },
    totalValue: {
      fontSize: isMobile ? '1.5rem' : '1.8rem',
      fontWeight: '800',
      color: '#c59b00',
    },
    checkoutButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      color: '#ffffff',
      border: 'none',
      padding: isMobile ? '1rem' : '1.2rem',
      borderRadius: '12px',
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    continueButton: {
      width: '100%',
      background: 'transparent',
      color: '#c59b00',
      border: '2px solid #c59b00',
      padding: isMobile ? '1rem' : '1.2rem',
      borderRadius: '12px',
      fontSize: isMobile ? '0.95rem' : '1rem',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    emptyCart: {
      textAlign: 'center',
      padding: isMobile ? '3rem 1rem' : '5rem 2rem',
      background: '#ffffff',
      borderRadius: '20px',
      maxWidth: '600px',
      margin: '0 auto',
      width: '100%',
    },
    emptyIcon: {
      marginBottom: '1.5rem',
    },
    emptyTitle: {
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '1rem',
    },
    emptyText: {
      fontSize: isMobile ? '0.95rem' : '1.1rem',
      color: '#666666',
      marginBottom: '2rem',
      lineHeight: '1.6',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '1rem',
    },
    modalContent: {
      background: '#ffffff',
      borderRadius: '20px',
      padding: isMobile ? '1.5rem' : '2rem',
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center',
      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    },
    modalTitle: {
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '1rem',
    },
    modalText: {
      fontSize: isMobile ? '0.95rem' : '1rem',
      color: '#666666',
      marginBottom: '1.5rem',
      lineHeight: '1.5',
    },
    modalButtons: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    modalButton: {
      padding: '0.8rem 1.5rem',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: 'none',
    },
    successNotif: {
      position: 'fixed',
      top: '100px',
      right: isMobile ? '50%' : '2rem',
      transform: isMobile ? 'translateX(50%)' : 'translateX(0)',
      background: 'linear-gradient(135deg, #00c851 0%, #007e33 100%)',
      color: '#ffffff',
      padding: isMobile ? '1rem 1.5rem' : '1rem 2rem',
      borderRadius: '12px',
      boxShadow: '0 10px 30px rgba(0,200,81,0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.8rem',
      zIndex: 3000,
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '600',
      animation: 'slideIn 0.5s ease',
    },
  };

  const CartItemComponent = ({ item }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isRemoving = removingId === item.id;

    return (
      <div
        style={{
          ...styles.cartItem,
          opacity: isRemoving ? 0 : 1,
          transform: isRemoving ? 'translateX(-100%)' : 'translateX(0)',
          borderColor: isHovered ? '#c59b00' : 'transparent',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img src={item.image} alt={item.name} style={styles.itemImage} />
        
        <div style={styles.itemDetails}>
          <div style={styles.itemCategory}>{item.category}</div>
          <div style={styles.itemName}>{item.name}</div>
          <div style={styles.itemQuantity}>
            <Package size={14} />
            {item.quantity}
          </div>
          <div style={styles.itemPrice}>{item.price.toFixed(2)}€</div>
        </div>

        <div style={styles.itemActions}>
          <button
            style={{
              ...styles.deleteButton,
              background: isHovered ? '#fff5f5' : 'transparent',
            }}
            onClick={() => handleRemoveItem(item.id)}
          >
            <Trash2 size={isMobile ? 18 : 20} />
          </button>

          <div style={styles.quantityControl}>
            <button
              style={styles.quantityButton}
              onClick={() => updateQuantity(item.id, item.cartQuantity - 1)}
            >
              <Minus size={16} />
            </button>
            <span style={styles.quantityValue}>{item.cartQuantity}</span>
            <button
              style={{
                ...styles.quantityButton,
                background: '#c59b00',
                color: '#ffffff',
              }}
              onClick={() => updateQuantity(item.id, item.cartQuantity + 1)}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CheckoutButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <button
        style={{
          ...styles.checkoutButton,
          background: isHovered
            ? 'linear-gradient(135deg, #c59b00 0%, #d4a800 100%)'
            : 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 8px 25px rgba(197,155,0,0.3)' : 'none',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => navigate('/checkout')}
      >
        Procéder au paiement
        <ArrowRight size={20} />
      </button>
    );
  };

  const ContinueButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <button
        style={{
          ...styles.continueButton,
          background: isHovered ? '#c59b00' : 'transparent',
          color: isHovered ? '#ffffff' : '#c59b00',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => navigate('/catalogue')}
      >
        Continuer mes achats
      </button>
    );
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(100px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>

      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>
          <ShoppingCart size={isMobile ? 32 : 40} />
          Mon Panier
        </h1>
        <p style={styles.heroSubtitle}>
          {cartItems.length} article{cartItems.length > 1 ? 's' : ''} dans votre panier
        </p>
      </div>

      <div style={styles.mainContent}>
        {cartItems.length === 0 ? (
          <div style={styles.emptyCart}>
            <div style={styles.emptyIcon}>
              <ShoppingCart size={80} color="#cccccc" />
            </div>
            <h2 style={styles.emptyTitle}>Votre panier est vide</h2>
            <p style={styles.emptyText}>
              Explorez notre catalogue et ajoutez des produits à votre panier pour commencer vos achats !
            </p>
            <button
              style={{
                ...styles.checkoutButton,
                width: 'auto',
                display: 'inline-flex',
              }}
              onClick={() => navigate('/catalogue')}
            >
              Découvrir le catalogue
              <ArrowRight size={20} />
            </button>
          </div>
        ) : (
          <>
            <div style={styles.cartSection}>
              <div style={styles.cartHeader}>
                <h2 style={styles.cartTitle}>Articles ({cartItems.length})</h2>
                <button
                  style={styles.clearButton}
                  onClick={() => setShowClearConfirm(true)}
                >
                  <Trash2 size={18} />
                  Vider le panier
                </button>
              </div>

              <div style={styles.cartItems}>
                {cartItems.map(item => (
                  <CartItemComponent key={item.id} item={item} />
                ))}
              </div>
            </div>

            <div style={styles.summarySection}>
              <div style={styles.summaryCard}>
                <h3 style={styles.summaryTitle}>Récapitulatif</h3>

                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Sous-total</span>
                  <span style={styles.summaryValue}>{total.toFixed(2)}€</span>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Mode d'envoi</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="radio" name="shipping" value="mondial" checked={shippingMethod === 'mondial'} onChange={() => setShippingMethod('mondial')} />
                      Mondial Relay
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="radio" name="shipping" value="colissimo" checked={shippingMethod === 'colissimo'} onChange={() => setShippingMethod('colissimo')} />
                      Colissimo
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="radio" name="shipping" value="dpd" checked={shippingMethod === 'dpd'} onChange={() => setShippingMethod('dpd')} />
                      DPD
                    </label>
                  </div>
                </div>

                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Frais de livraison</span>
                  <span style={styles.summaryValue}>{fraisLivraison.toFixed(2)}€</span>
                </div>

                <div style={styles.totalRow}>
                  <span style={styles.totalLabel}>Total</span>
                  <span style={styles.totalValue}>{totalFinal.toFixed(2)}€</span>
                </div>

                <CheckoutButton />
                <ContinueButton />
              </div>
            </div>
          </>
        )}
      </div>

      {showClearConfirm && (
        <div style={styles.modal} onClick={() => setShowClearConfirm(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Vider le panier ?</h3>
            <p style={styles.modalText}>
              Êtes-vous sûr de vouloir supprimer tous les articles de votre panier ?
            </p>
            <div style={styles.modalButtons}>
              <button
                style={{
                  ...styles.modalButton,
                  background: '#f0f0f0',
                  color: '#000000',
                }}
                onClick={() => setShowClearConfirm(false)}
              >
                Annuler
              </button>
              <button
                style={{
                  ...styles.modalButton,
                  background: '#ff4444',
                  color: '#ffffff',
                }}
                onClick={handleClearCart}
              >
                Vider
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div style={styles.successNotif}>
          <Check size={24} />
          Panier vidé avec succès !
        </div>
      )}
    </div>
  );
};

export default Cart;